import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Divider,
  InputAdornment,
  IconButton,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem as MuiMenuItem
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import StoreIcon from '@mui/icons-material/Store';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useAuth } from '../../../contexts/AuthContext';
import { mainApi, hepsijetApi, mngCargoApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  PlatformIntegration,
  STORE_NAME_MAX_LENGTH,
  resolveStoreLabel,
} from '../../../types/platformIntegration';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`integration-tabpanel-${index}`}
      aria-labelledby={`integration-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface AddIntegrationModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  onSaved: () => void;
  category?: 'marketplace' | 'food' | 'cargo';
  /** Listedeki mevcut kayıtlar; aynı şube + platformda tanımlı mağazaları uyarmak için. */
  existingIntegrations?: PlatformIntegration[];
}

// Platform bilgisi interface
interface PlatformInfo {
  key: Platform;
  label: string;
  color: string;
}

/**
 * Eklenmekte olan tek bir entegrasyon kaydı.
 *
 * Eskiden form durumu platform adıyla anahtarlanmış bir sözlüktü; bu yüzden
 * aynı platformdan ikinci bir kayıt açmak yapısal olarak mümkün değildi.
 * Artık form "şu an eklenmekte olan kayıt" üzerinde çalışır, sekme yalnızca
 * hangi platformun alan setinin gösterileceğini seçer.
 */
interface IntegrationFormData {
  platform: Platform;
  enabled: boolean;
  storeName: string;
  credentials: {
    apiKey: string;
    apiSecret: string;
    sellerId: string;
    restaurantId?: string;
    appSecretKey?: string;
    restaurantSecretKey?: string;
  };
}

// Platform bilgileri
const MARKETPLACE_PLATFORMS: PlatformInfo[] = [
  { key: 'trendyol', label: 'Trendyol', color: '#f27a1a' },
  { key: 'n11', label: 'N11', color: '#7b1fa2' },
  { key: 'hepsiburada', label: 'Hepsiburada', color: '#ff6000' },
  { key: 'amazon', label: 'Amazon', color: '#ff9900' },
  { key: 'pazarama', label: 'Pazarama', color: '#e6007e' },
  { key: 'pttavm', label: 'PttAVM', color: '#e30613' },
];

const FOOD_PLATFORMS: PlatformInfo[] = [
  { key: 'yemeksepeti', label: 'Yemeksepeti', color: '#e31e24' },
  { key: 'getir', label: 'Getir Food', color: '#5d3ebc' },
  { key: 'migros_yemek', label: 'Migros Food', color: '#ff6600' },
  { key: 'trendyol_yemek', label: 'Trendyol Food', color: '#f27a1a' },
];

const CARGO_PLATFORMS: PlatformInfo[] = [
  { key: 'hepsijet', label: 'HepsiJet', color: '#ff6000' },
  { key: 'mng', label: 'DHL (MNG)', color: '#FFCC00' },
];

const AddIntegrationModal: React.FC<AddIntegrationModalProps> = ({
  open,
  onClose,
  companyId,
  onSaved,
  category = 'marketplace',
  existingIntegrations = []
}) => {
  const { t } = useTranslation('salesSystem');
  const { branches } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showApiSecret, setShowApiSecret] = useState<{[key: string]: boolean}>({});
  const [selectedBranchId, setSelectedBranchId] = useState<string>('');

  // HepsiJet merchant config (firma-özel sabit değerler; HepsiJet tarafından verilir).
  // sendDeliveryOrderEnhanced payload'ı tamamen bunlardan oluşur, hardcode yok.
  const emptyHepsijetMerchant = {
    companyName: '',
    abbreviationCode: '',
    companyAddressId: '',
    senderCountry: 'Türkiye',
    senderCity: '',
    senderTown: '',
    senderDistrict: '',
    senderAddressLine1: '',
    currentXDockCode: ''
  };
  const [hepsijetMerchant, setHepsijetMerchant] = useState({ ...emptyHepsijetMerchant });

  // MNG (DHL) merchant config: gonderici adresi + ortam (tenant DB).
  const emptyMngMerchant = {
    senderName: '',
    senderPhone: '',
    senderCountry: 'Türkiye',
    senderCity: '',
    senderDistrict: '',
    senderNeighborhood: '',
    senderAddressLine: '',
    isTestMode: true
  };
  const [mngMerchant, setMngMerchant] = useState({ ...emptyMngMerchant });

  // MNG Apizone platform credential (CargoProviderCredentials — ANA DB).
  const emptyMngPlatform = {
    clientId: '',
    clientSecret: '',
    apiBaseUrl: 'https://testapi.mngkargo.com.tr',
    hasClientSecret: false
  };
  const [mngPlatform, setMngPlatform] = useState({ ...emptyMngPlatform });
  const [showMngClientSecret, setShowMngClientSecret] = useState(false);

  // Gerçek şubeler (null olmayan)
  const realBranches = branches.filter(b => b.id !== 'null');

  const getBranchName = (branchId?: string | null) =>
    branches.find(b => b.id === branchId)?.name || '';
  
  // Snackbar State
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Kategoriye göre platformları belirle
  const platforms = category === 'marketplace'
    ? MARKETPLACE_PLATFORMS
    : category === 'cargo'
    ? CARGO_PLATFORMS
    : FOOD_PLATFORMS;

  // Boş taslak kayıt. Her platform için ayrı slot tutulmaz; form tek bir kayıt üzerinde çalışır.
  const createEmptyDraft = (platform: Platform): IntegrationFormData => ({
    platform,
    enabled: true,
    storeName: '',
    credentials: {
      apiKey: '',
      apiSecret: '',
      sellerId: '',
      restaurantId: '',
      appSecretKey: '',
      restaurantSecretKey: ''
    }
  });

  const [formData, setFormData] = useState<IntegrationFormData>(() => createEmptyDraft(platforms[0].key));

  // Kategori değiştiğinde tab, şube seçimini ve taslağı sıfırla
  useEffect(() => {
    setActiveTab(0);
    setSelectedBranchId('');
    setHepsijetMerchant({ ...emptyHepsijetMerchant });
    setMngMerchant({ ...emptyMngMerchant });
    setFormData(createEmptyDraft(platforms[0].key));
    setMngPlatform({ ...emptyMngPlatform });
    setShowMngClientSecret(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const handleMerchantChange = (field: string, value: string) => {
    setHepsijetMerchant(prev => ({ ...prev, [field]: value }));
  };

  const handleMngPlatformChange = (field: string, value: string) => {
    setMngPlatform(prev => ({ ...prev, [field]: value }));
  };

  const handleMngMerchantChange = (field: string, value: string | boolean) => {
    setMngMerchant(prev => ({ ...prev, [field]: value }));
  };

  // MNG sekmesi acikken mevcut Apizone ayarini cek (varsa formu doldur).
  useEffect(() => {
    if (!open || category !== 'cargo') return;
    const platform = platforms[activeTab]?.key;
    if (platform !== 'mng') return;

    let cancelled = false;
    (async () => {
      try {
        const res = await mngCargoApi.get('/api/MngCargo/platform-config', {
          params: { isTestMode: mngMerchant.isTestMode }
        });
        const d = res.data?.Data ?? res.data?.data ?? {};
        if (cancelled) return;
        setMngPlatform({
          clientId: d.clientId ?? d.ClientId ?? '',
          clientSecret: '',
          apiBaseUrl: d.apiBaseUrl ?? d.ApiBaseUrl
            ?? (mngMerchant.isTestMode ? 'https://testapi.mngkargo.com.tr' : 'https://api.mngkargo.com.tr'),
          hasClientSecret: !!(d.hasClientSecret ?? d.HasClientSecret)
        });
      } catch {
        if (!cancelled) {
          setMngPlatform({
            ...emptyMngPlatform,
            apiBaseUrl: mngMerchant.isTestMode
              ? 'https://testapi.mngkargo.com.tr'
              : 'https://api.mngkargo.com.tr'
          });
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, category, activeTab, mngMerchant.isTestMode]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setError(null);
    // Taslak tek kayıt olduğu için platform değişince alanlar temizlenir;
    // aksi halde önceki platformun kimlik bilgileri yeni kayda sızardı.
    setFormData(createEmptyDraft(platforms[newValue].key));
  };

  const handleInputChange = (platform: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: field === 'credentials'
        ? { ...prev.credentials, ...value }
        : value
    }));
  };

  const toggleShowApiSecret = (platform: string) => {
    setShowApiSecret(prev => ({
      ...prev,
      [platform]: !prev[platform]
    }));
  };

  const validateForm = (platform: string): boolean => {
    const data = formData;
    const isHepsiburada = platform === 'hepsiburada';

    // Kargo platformları: credential + merchant config zorunlu
    if (CARGO_PLATFORMS.some(p => p.key === platform)) {
      if (!data.credentials.apiKey?.trim()) {
        setError(platform === 'mng'
          ? t('addIntegrationModal.errors.mngCustomerNumberRequired')
          : t('addIntegrationModal.errors.usernameRequired'));
        return false;
      }
      if (!data.credentials.apiSecret?.trim()) {
        setError(t('addIntegrationModal.errors.passwordRequired'));
        return false;
      }

      if (platform === 'hepsijet') {
        const m = hepsijetMerchant;
        const requiredMerchant: Array<[string, string]> = [
          [m.companyName, t('addIntegrationModal.form.merchantCompanyName')],
          [m.abbreviationCode, t('addIntegrationModal.form.merchantAbbreviationCode')],
          [m.companyAddressId, t('addIntegrationModal.form.merchantCompanyAddressId')],
          [m.senderCity, t('addIntegrationModal.form.merchantSenderCity')],
          [m.senderTown, t('addIntegrationModal.form.merchantSenderTown')],
          [m.senderAddressLine1, t('addIntegrationModal.form.merchantSenderAddress')],
          [m.currentXDockCode, t('addIntegrationModal.form.merchantXDockCode')]
        ];
        const missing = requiredMerchant.find(([val]) => !val?.trim());
        if (missing) {
          setError(t('addIntegrationModal.errors.merchantFieldRequired', { field: missing[1] }));
          return false;
        }
      } else if (platform === 'mng') {
        if (!mngPlatform.clientId?.trim()) {
          setError(t('addIntegrationModal.errors.mngClientIdRequired'));
          return false;
        }
        if (!mngPlatform.clientSecret?.trim() && !mngPlatform.hasClientSecret) {
          setError(t('addIntegrationModal.errors.mngClientSecretRequired'));
          return false;
        }
        const m = mngMerchant;
        const requiredMerchant: Array<[string, string]> = [
          [m.senderName, t('addIntegrationModal.form.mngSenderName')],
          [m.senderPhone, t('addIntegrationModal.form.mngSenderPhone')],
          [m.senderCity, t('addIntegrationModal.form.mngSenderCity')],
          [m.senderDistrict, t('addIntegrationModal.form.mngSenderDistrict')],
          [m.senderAddressLine, t('addIntegrationModal.form.mngSenderAddress')]
        ];
        const missing = requiredMerchant.find(([val]) => !val?.trim());
        if (missing) {
          setError(t('addIntegrationModal.errors.merchantFieldRequired', { field: missing[1] }));
          return false;
        }
      }
      return true;
    }

    // Yemek platformları için özel validasyon
    if (FOOD_PLATFORMS.some(p => p.key === platform)) {
      const isMigros = platform === 'migros_yemek';
      const isYemeksepeti = platform === 'yemeksepeti';
      const isTrendyolYemek = platform === 'trendyol_yemek';
      if (!selectedBranchId) {
        setError(t('addIntegrationModal.errors.branchRequired'));
        return false;
      }
      if (!data.storeName.trim()) {
        setError(t('addIntegrationModal.errors.storeNameRequired'));
        return false;
      }
      if (data.storeName.trim().length > STORE_NAME_MAX_LENGTH) {
        setError(t('addIntegrationModal.errors.storeNameTooLong', { max: STORE_NAME_MAX_LENGTH }));
        return false;
      }
      if (isTrendyolYemek) {
        if (!data.credentials.sellerId?.trim()) { setError(t('addIntegrationModal.errors.supplierIdRequired')); return false; }
        if (!data.credentials.restaurantId?.trim()) { setError(t('addIntegrationModal.errors.storeIdRequired')); return false; }
        if (!data.credentials.apiKey?.trim()) { setError(t('addIntegrationModal.errors.apiKeyRequired')); return false; }
        if (!data.credentials.apiSecret?.trim()) { setError(t('addIntegrationModal.errors.apiSecretRequired')); return false; }
        return true;
      }
      if (!data.credentials.restaurantId?.trim()) {
        setError(isMigros ? t('addIntegrationModal.errors.restaurantStoreIdRequired') : isYemeksepeti ? t('addIntegrationModal.errors.remoteIdRequired') : t('addIntegrationModal.errors.restaurantIdRequired'));
        return false;
      }
      if (!data.credentials.appSecretKey?.trim()) {
        setError(isMigros ? t('addIntegrationModal.errors.xApiKeyRequired') : isYemeksepeti ? t('addIntegrationModal.errors.chainCodeRequired') : t('addIntegrationModal.errors.appSecretRequired'));
        return false;
      }
      if (!isYemeksepeti && !data.credentials.restaurantSecretKey?.trim()) {
        setError(isMigros ? t('addIntegrationModal.errors.chainIdRequired') : t('addIntegrationModal.errors.restaurantSecretRequired'));
        return false;
      }
      return true;
    }
    
    // Pazaryeri platformları için validasyon
    if (!isHepsiburada && !data.credentials.apiKey.trim()) {
      setError(t('addIntegrationModal.errors.apiKeyRequired'));
      return false;
    }
    
    if (!data.credentials.apiSecret.trim()) {
      setError(t('addIntegrationModal.errors.apiSecretRequired'));
      return false;
    }
    
    if (['trendyol', 'hepsiburada', 'amazon'].includes(platform) && !data.credentials.sellerId.trim()) {
      setError(t('addIntegrationModal.errors.sellerIdRequired'));
      return false;
    }
    
    return true;
  };

  const handleSave = async () => {
    if (!companyId) {
      setError(t('addIntegrationModal.errors.companyInfoNotFound'));
      return;
    }

    const currentPlatform = platforms[activeTab].key;
    
    if (!validateForm(currentPlatform)) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const isFoodPlatform = FOOD_PLATFORMS.some(p => p.key === currentPlatform);
      const isCargoPlatform = CARGO_PLATFORMS.some(p => p.key === currentPlatform);
      const currentCategory = isFoodPlatform ? 'food' : isCargoPlatform ? 'cargo' : 'marketplace';
      
      const requestBody = {
        companyId,
        branchId: isFoodPlatform ? selectedBranchId : null,
        platform: currentPlatform,
        category: currentCategory,
        isEnabled: formData.enabled,
        // Mağaza/marka adı yalnızca yemek platformlarında anlamlı; diğerlerinde gönderilmez.
        storeName: isFoodPlatform ? formData.storeName.trim() : null,
            apiKey: currentPlatform === 'hepsiburada'
              ? null
              : (formData.credentials.apiKey || null),
        apiSecret: formData.credentials.apiSecret || null,
        sellerId: formData.credentials.sellerId || null,
        restaurantId: formData.credentials.restaurantId || null,
        appSecretKey: formData.credentials.appSecretKey || null,
        restaurantSecretKey: formData.credentials.restaurantSecretKey || null
      };
      
      console.log(`🚀 [${currentPlatform.toUpperCase()}] .NET API'ye kaydediliyor...`, requestBody);
      
      const response = await mainApi.post('/api/platformintegration/create', requestBody);
      const result = response.data;

      if (!result.success) {
        throw new Error(result.message || t('addIntegrationModal.errors.saveFailed'));
      }

      // Kargo: credential kaydından sonra firma-ozel merchant config'i tenant DB'ye yaz.
      if (currentPlatform === 'hepsijet') {
        await hepsijetApi.post('/api/hepsijet/merchant-config', {
          companyName: hepsijetMerchant.companyName.trim(),
          abbreviationCode: hepsijetMerchant.abbreviationCode.trim(),
          companyAddressId: hepsijetMerchant.companyAddressId.trim(),
          senderCountry: hepsijetMerchant.senderCountry?.trim() || 'Türkiye',
          senderCity: hepsijetMerchant.senderCity.trim(),
          senderTown: hepsijetMerchant.senderTown.trim(),
          senderDistrict: hepsijetMerchant.senderDistrict?.trim() || null,
          senderAddressLine1: hepsijetMerchant.senderAddressLine1.trim(),
          currentXDockCode: hepsijetMerchant.currentXDockCode.trim()
        });
      } else if (currentPlatform === 'mng') {
        const platformPayload: Record<string, unknown> = {
          ClientId: mngPlatform.clientId.trim(),
          IsTestMode: mngMerchant.isTestMode
        };
        if (mngPlatform.clientSecret?.trim()) {
          platformPayload.ClientSecret = mngPlatform.clientSecret.trim();
        }
        const platformRes = await mngCargoApi.post('/api/MngCargo/platform-config', platformPayload);
        const platformOk = platformRes.data?.Success ?? platformRes.data?.success;
        if (!platformOk) {
          throw new Error(
            platformRes.data?.Message || platformRes.data?.message
            || t('addIntegrationModal.errors.mngPlatformSaveFailed')
          );
        }

        await mngCargoApi.post('/api/MngCargo/merchant-config', {
          SenderName: mngMerchant.senderName.trim(),
          SenderPhone: mngMerchant.senderPhone.trim(),
          SenderCountry: mngMerchant.senderCountry?.trim() || 'Türkiye',
          SenderCity: mngMerchant.senderCity.trim(),
          SenderDistrict: mngMerchant.senderDistrict.trim(),
          SenderNeighborhood: mngMerchant.senderNeighborhood?.trim() || null,
          SenderAddressLine: mngMerchant.senderAddressLine.trim(),
          IsTestMode: mngMerchant.isTestMode
        });
      }

      console.log(`✅ [${currentPlatform.toUpperCase()}] .NET API'ye kaydedildi!`, result);

      setSnackbar({
        open: true,
        message: t('addIntegrationModal.messages.integrationAdded', { platform: platforms.find(p => p.key === currentPlatform)?.label }),
        severity: 'success'
      });
      
      setTimeout(() => {
        onSaved();
        handleClose();
      }, 1500);
    } catch (error: any) {
      console.error('Error while saving integration:', error);
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.Message ||
        error?.message;
      setError(backendMessage || t('addIntegrationModal.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setActiveTab(0);
      setError(null);
      setSelectedBranchId('');
      setHepsijetMerchant({ ...emptyHepsijetMerchant });
      setMngMerchant({ ...emptyMngMerchant });
      setMngPlatform({ ...emptyMngPlatform });
      setShowMngClientSecret(false);
      setSnackbar(prev => ({ ...prev, open: false }));
      setFormData(createEmptyDraft(platforms[0].key));
      onClose();
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const renderPlatformForm = (platform: string) => {
    const data = formData;
    const platformInfo = [...MARKETPLACE_PLATFORMS, ...FOOD_PLATFORMS, ...CARGO_PLATFORMS].find(p => p.key === platform);
    const isFoodPlatform = FOOD_PLATFORMS.some(p => p.key === platform);

    // Seçili şube + platform için zaten tanımlı mağazalar. Kullanıcı burada
    // mevcut bir kaydı güncellemediğini, yeni bir mağaza açtığını görmeli.
    const existingStoreLabels = isFoodPlatform && selectedBranchId
      ? existingIntegrations
          .filter(i => i.platform === platform && (i.branchId || '') === selectedBranchId)
          .map(i => resolveStoreLabel(i, existingIntegrations, getBranchName(i.branchId)))
          .filter(label => label.length > 0)
      : [];

    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2, color: platformInfo?.color }}>
          {t('addIntegrationModal.form.integrationInformation', { platform: platformInfo?.label })}
        </Typography>

        {/* Genel Ayarlar */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            {t('addIntegrationModal.form.generalSettings')}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={data.enabled}
                onChange={(e) => handleInputChange(platform, 'enabled', e.target.checked)}
              />
            }
            label={t('addIntegrationModal.form.enableIntegration')}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* API Bilgileri */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            {t('addIntegrationModal.form.apiInformation')}
          </Typography>
          
          {platform === 'hepsijet' ? (
            <>
              <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
                {t('addIntegrationModal.form.hepsijetCredentialHint')}
              </Alert>
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.username')}
                value={data.credentials.apiKey}
                onChange={(e) => handleInputChange(platform, 'credentials', { apiKey: e.target.value })}
                margin="normal"
                required
                helperText={t('addIntegrationModal.form.usernameHelper')}
              />
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.password')}
                type={showApiSecret[platform] ? 'text' : 'password'}
                value={data.credentials.apiSecret}
                onChange={(e) => handleInputChange(platform, 'credentials', { apiSecret: e.target.value })}
                margin="normal"
                required
                helperText={t('addIntegrationModal.form.passwordHelper')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => toggleShowApiSecret(platform)} edge="end">
                        {showApiSecret[platform] ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                {t('addIntegrationModal.form.merchantSectionTitle')}
              </Typography>
              <Alert severity="warning" sx={{ mb: 1 }}>
                {t('addIntegrationModal.form.merchantSectionHint')}
              </Alert>

              <TextField
                fullWidth
                label={t('addIntegrationModal.form.merchantCompanyName')}
                value={hepsijetMerchant.companyName}
                onChange={(e) => handleMerchantChange('companyName', e.target.value)}
                margin="normal"
                required
                helperText={t('addIntegrationModal.form.merchantCompanyNameHelper')}
              />
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.merchantAbbreviationCode')}
                value={hepsijetMerchant.abbreviationCode}
                onChange={(e) => handleMerchantChange('abbreviationCode', e.target.value)}
                margin="normal"
                required
                helperText={t('addIntegrationModal.form.merchantAbbreviationCodeHelper')}
              />
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.merchantCompanyAddressId')}
                value={hepsijetMerchant.companyAddressId}
                onChange={(e) => handleMerchantChange('companyAddressId', e.target.value)}
                margin="normal"
                required
                helperText={t('addIntegrationModal.form.merchantCompanyAddressIdHelper')}
              />
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.merchantXDockCode')}
                value={hepsijetMerchant.currentXDockCode}
                onChange={(e) => handleMerchantChange('currentXDockCode', e.target.value)}
                margin="normal"
                required
                helperText={t('addIntegrationModal.form.merchantXDockCodeHelper')}
              />

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 'bold' }}>
                {t('addIntegrationModal.form.merchantSenderAddressTitle')}
              </Typography>
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.merchantSenderCity')}
                value={hepsijetMerchant.senderCity}
                onChange={(e) => handleMerchantChange('senderCity', e.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.merchantSenderTown')}
                value={hepsijetMerchant.senderTown}
                onChange={(e) => handleMerchantChange('senderTown', e.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.merchantSenderDistrict')}
                value={hepsijetMerchant.senderDistrict}
                onChange={(e) => handleMerchantChange('senderDistrict', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.merchantSenderAddress')}
                value={hepsijetMerchant.senderAddressLine1}
                onChange={(e) => handleMerchantChange('senderAddressLine1', e.target.value)}
                margin="normal"
                required
                multiline
                minRows={2}
              />
            </>
          ) : platform === 'mng' ? (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={mngMerchant.isTestMode}
                    onChange={(e) => handleMngMerchantChange('isTestMode', e.target.checked)}
                  />
                }
                label={t('addIntegrationModal.form.mngTestMode')}
              />

              <Typography variant="subtitle1" sx={{ mt: 2, mb: 0.5, fontWeight: 'bold' }}>
                {t('addIntegrationModal.form.mngApizoneSectionTitle')}
              </Typography>
              <Alert severity="info" sx={{ mb: 1 }}>
                {t('addIntegrationModal.form.mngApizoneHint')}
              </Alert>
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.mngClientId')}
                value={mngPlatform.clientId}
                onChange={(e) => handleMngPlatformChange('clientId', e.target.value)}
                margin="normal"
                required
                helperText={t('addIntegrationModal.form.mngClientIdHelper')}
              />
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.mngClientSecret')}
                type={showMngClientSecret ? 'text' : 'password'}
                value={mngPlatform.clientSecret}
                onChange={(e) => handleMngPlatformChange('clientSecret', e.target.value)}
                margin="normal"
                required={!mngPlatform.hasClientSecret}
                helperText={mngPlatform.hasClientSecret
                  ? t('addIntegrationModal.form.mngClientSecretKeepHelper')
                  : t('addIntegrationModal.form.mngClientSecretHelper')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowMngClientSecret(v => !v)} edge="end">
                        {showMngClientSecret ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.mngApiBaseUrl')}
                value={mngPlatform.apiBaseUrl}
                margin="normal"
                InputProps={{ readOnly: true }}
                helperText={t('addIntegrationModal.form.mngApiBaseUrlHelper')}
              />

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                {t('addIntegrationModal.form.mngMerchantCredentialTitle')}
              </Typography>
              <Alert severity="info" sx={{ mb: 1 }}>
                {t('addIntegrationModal.form.mngCredentialHint')}
              </Alert>
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.mngCustomerNumber')}
                value={data.credentials.apiKey}
                onChange={(e) => handleInputChange(platform, 'credentials', { apiKey: e.target.value })}
                margin="normal"
                required
                helperText={t('addIntegrationModal.form.mngCustomerNumberHelper')}
              />
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.mngPassword')}
                type={showApiSecret[platform] ? 'text' : 'password'}
                value={data.credentials.apiSecret}
                onChange={(e) => handleInputChange(platform, 'credentials', { apiSecret: e.target.value })}
                margin="normal"
                required
                helperText={t('addIntegrationModal.form.mngPasswordHelper')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => toggleShowApiSecret(platform)} edge="end">
                        {showApiSecret[platform] ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                {t('addIntegrationModal.form.mngMerchantSectionTitle')}
              </Typography>
              <Alert severity="warning" sx={{ mb: 1 }}>
                {t('addIntegrationModal.form.mngMerchantSectionHint')}
              </Alert>

              <TextField
                fullWidth
                label={t('addIntegrationModal.form.mngSenderName')}
                value={mngMerchant.senderName}
                onChange={(e) => handleMngMerchantChange('senderName', e.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.mngSenderPhone')}
                value={mngMerchant.senderPhone}
                onChange={(e) => handleMngMerchantChange('senderPhone', e.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.mngSenderCity')}
                value={mngMerchant.senderCity}
                onChange={(e) => handleMngMerchantChange('senderCity', e.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.mngSenderDistrict')}
                value={mngMerchant.senderDistrict}
                onChange={(e) => handleMngMerchantChange('senderDistrict', e.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.mngSenderNeighborhood')}
                value={mngMerchant.senderNeighborhood}
                onChange={(e) => handleMngMerchantChange('senderNeighborhood', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.mngSenderAddress')}
                value={mngMerchant.senderAddressLine}
                onChange={(e) => handleMngMerchantChange('senderAddressLine', e.target.value)}
                margin="normal"
                required
                multiline
                minRows={2}
              />
            </>
          ) : isFoodPlatform ? (
            // Yemek platformları için form
            <>
              {/* Şube Seçimi */}
              <FormControl fullWidth margin="normal" required>
                <InputLabel id="branch-select-label">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <StoreIcon fontSize="small" />
                    {t('addIntegrationModal.form.selectBranch')}
                  </Box>
                </InputLabel>
                <Select
                  labelId="branch-select-label"
                  value={selectedBranchId}
                  label={t('addIntegrationModal.form.selectBranch')}
                  onChange={(e) => setSelectedBranchId(e.target.value)}
                >
                  {realBranches.length === 0 ? (
                    <MuiMenuItem disabled value="">
                      <em>{t('addIntegrationModal.form.noBranchFound')}</em>
                    </MuiMenuItem>
                  ) : (
                    realBranches.map((branch) => (
                      <MuiMenuItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </MuiMenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              {realBranches.length === 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {t('addIntegrationModal.form.noBranchDefined')}
                </Alert>
              )}

              {/* Mağaza / Marka adı: aynı şubede birden fazla mağaza olduğunda siparişleri ayırır */}
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.storeName')}
                value={data.storeName}
                onChange={(e) => handleInputChange(platform, 'storeName', e.target.value)}
                margin="normal"
                required
                inputProps={{ maxLength: STORE_NAME_MAX_LENGTH }}
                helperText={t('addIntegrationModal.form.storeNameHelper')}
              />

              {existingStoreLabels.length > 0 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {t('addIntegrationModal.form.existingStoresNotice', {
                    count: existingStoreLabels.length,
                    platform: platformInfo?.label,
                    stores: existingStoreLabels.join(', ')
                  })}
                </Alert>
              )}

              {platform === 'trendyol_yemek' ? (
                <>
                  <TextField
                    fullWidth
                    label={t('addIntegrationModal.form.supplierId')}
                    value={data.credentials.sellerId || ''}
                    onChange={(e) => handleInputChange(platform, 'credentials', { sellerId: e.target.value })}
                    margin="normal"
                    required
                    helperText={t('addIntegrationModal.form.supplierIdHelper')}
                  />
                  <TextField
                    fullWidth
                    label={t('addIntegrationModal.form.storeId')}
                    value={data.credentials.restaurantId || ''}
                    onChange={(e) => handleInputChange(platform, 'credentials', { restaurantId: e.target.value })}
                    margin="normal"
                    required
                    helperText={t('addIntegrationModal.form.storeIdHelper')}
                  />
                  <TextField
                    fullWidth
                    label={t('addIntegrationModal.form.apiKey')}
                    value={data.credentials.apiKey || ''}
                    onChange={(e) => handleInputChange(platform, 'credentials', { apiKey: e.target.value })}
                    margin="normal"
                    required
                    helperText={t('addIntegrationModal.form.apiKeyHelperTrendyolFood')}
                  />
                  <TextField
                    fullWidth
                    label={t('addIntegrationModal.form.apiSecret')}
                    type={showApiSecret[platform] ? 'text' : 'password'}
                    value={data.credentials.apiSecret || ''}
                    onChange={(e) => handleInputChange(platform, 'credentials', { apiSecret: e.target.value })}
                    margin="normal"
                    required
                    helperText={t('addIntegrationModal.form.apiSecretHelperTrendyolFood')}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => toggleShowApiSecret(platform)} edge="end">
                            {showApiSecret[platform] ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </>
              ) : (
                <>
                  <TextField
                    fullWidth
                    label={platform === 'migros_yemek' ? t('addIntegrationModal.form.restaurantStoreId') : platform === 'yemeksepeti' ? t('addIntegrationModal.form.remoteId') : t('addIntegrationModal.form.restaurantId')}
                    value={data.credentials.restaurantId || ''}
                    onChange={(e) => handleInputChange(platform, 'credentials', { restaurantId: e.target.value })}
                    margin="normal"
                    required
                    helperText={platform === 'migros_yemek'
                      ? 'Your Migros Food store identifier (StoreId)'
                      : platform === 'yemeksepeti'
                      ? t('addIntegrationModal.form.remoteIdHelper')
                      : t('addIntegrationModal.form.restaurantIdentifierHelper', { platform: platformInfo?.label })}
                  />
                  <TextField
                    fullWidth
                    label={platform === 'migros_yemek' ? t('addIntegrationModal.form.xApiKey') : platform === 'yemeksepeti' ? t('addIntegrationModal.form.chainCode') : t('addIntegrationModal.form.appSecretKey')}
                    type={showApiSecret[platform] ? 'text' : 'password'}
                    value={data.credentials.appSecretKey || ''}
                    onChange={(e) => handleInputChange(platform, 'credentials', { appSecretKey: e.target.value })}
                    margin="normal"
                    required
                    helperText={platform === 'migros_yemek'
                      ? 'API key provided specifically for your restaurant by Migros Food (XApiKey)'
                      : platform === 'yemeksepeti'
                      ? t('addIntegrationModal.form.chainCodeHelper')
                      : t('addIntegrationModal.form.applicationSecretKeyHelper', { platform: platformInfo?.label })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => toggleShowApiSecret(platform)} edge="end">
                            {showApiSecret[platform] ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {platform !== 'yemeksepeti' && (
                  <TextField
                    fullWidth
                    label={platform === 'migros_yemek' ? t('addIntegrationModal.form.chainId') : t('addIntegrationModal.form.restaurantSecretKey')}
                    type={platform === 'migros_yemek' ? 'text' : (showApiSecret[platform] ? 'text' : 'password')}
                    value={data.credentials.restaurantSecretKey || ''}
                    onChange={(e) => handleInputChange(platform, 'credentials', { restaurantSecretKey: e.target.value })}
                    margin="normal"
                    required
                    helperText={platform === 'migros_yemek'
                      ? 'Your Migros Food chain identifier (StoreGroupId)'
                      : t('addIntegrationModal.form.restaurantSecretKeyHelper', { platform: platformInfo?.label })}
                    InputProps={platform !== 'migros_yemek' ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => toggleShowApiSecret(platform)} edge="end">
                            {showApiSecret[platform] ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    } : undefined}
                  />
                  )}
                </>
              )}
            </>
          ) : (
            // Pazaryeri platformları için form
            <>
              {platform === 'hepsiburada' && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Hepsiburada icin yalnizca Secret Key ve MerchantId gereklidir.
                </Alert>
              )}
              {platform === 'trendyol' && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {t('addIntegrationModal.form.trendyolCredentialHint')}
                </Alert>
              )}
              {platform === 'pazarama' && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Pazarama icin Client ID, Client Secret ve Satici ID gereklidir. Bu bilgileri
                  isortagim.pazarama.com panelinde "Hesap Bilgileri" alanindan temin edebilirsiniz.
                </Alert>
              )}
              {platform === 'pttavm' && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  PttAVM icin Api-Key ve Access-Token gereklidir. Bu bilgileri PttAVM
                  magaza panelinizdeki entegrasyon ayarlarindan temin edebilirsiniz.
                </Alert>
              )}

              {platform !== 'hepsiburada' && (
                <TextField
                  fullWidth
                  label={platform === 'pazarama' ? 'Client ID' : platform === 'pttavm' ? 'Api-Key' : t('addIntegrationModal.form.apiKey')}
                  value={data.credentials.apiKey}
                  onChange={(e) => handleInputChange(platform, 'credentials', { apiKey: e.target.value })}
                  margin="normal"
                  required
                  helperText={platform === 'pazarama'
                    ? 'Pazarama Hesap Bilgileri ekranindaki clientId degerini girin.'
                    : platform === 'pttavm'
                    ? 'PttAVM Api-Key degerini girin.'
                    : t('addIntegrationModal.form.apiKeyHelper', { platform: platformInfo?.label })}
                />
              )}
              
              <TextField
                fullWidth
                label={platform === 'pazarama' ? 'Client Secret' : platform === 'pttavm' ? 'Access-Token' : t('addIntegrationModal.form.apiSecret')}
                type={showApiSecret[platform] ? 'text' : 'password'}
                value={data.credentials.apiSecret}
                onChange={(e) => handleInputChange(platform, 'credentials', { apiSecret: e.target.value })}
                margin="normal"
                required
                helperText={platform === 'hepsiburada'
                  ? 'Hepsiburada panelindeki Servis Anahtari (SecretKey) degerini girin.'
                  : platform === 'pazarama'
                  ? 'Pazarama Hesap Bilgileri ekranindaki clientSecret degerini girin.'
                  : platform === 'pttavm'
                  ? 'PttAVM Access-Token degerini girin.'
                  : t('addIntegrationModal.form.apiSecretHelper', { platform: platformInfo?.label })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => toggleShowApiSecret(platform)}
                        edge="end"
                      >
                        {showApiSecret[platform] ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {['trendyol', 'hepsiburada', 'amazon'].includes(platform) && (
                <TextField
                  fullWidth
                  label={platform === 'hepsiburada'
                    ? 'MerchantId (Magaza ID)'
                    : (platform === 'trendyol' ? t('addIntegrationModal.form.sellerIdCurrentAccount') : t('addIntegrationModal.form.sellerId'))}
                  value={data.credentials.sellerId}
                  onChange={(e) => handleInputChange(platform, 'credentials', { sellerId: e.target.value })}
                  margin="normal"
                  required
                  helperText={platform === 'hepsiburada'
                    ? 'Hepsiburada magaza MerchantId degerini girin.'
                    : t('addIntegrationModal.form.sellerIdentifierHelper', { platform: platformInfo?.label })}
                />
              )}
            </>
          )}
        </Box>
      </Box>
    );
  };

  const getDialogTitle = () => {
    if (category === 'marketplace') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StorefrontIcon color="primary" />
          <Typography variant="h6">{t('addIntegrationModal.title.marketplace')}</Typography>
        </Box>
      );
    }
    if (category === 'cargo') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalShippingIcon sx={{ color: '#ff6000' }} />
          <Typography variant="h6">{t('addIntegrationModal.title.cargo')}</Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <RestaurantIcon color="secondary" />
        <Typography variant="h6">{t('addIntegrationModal.title.food')}</Typography>
      </Box>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '550px' }
      }}
    >
      <DialogTitle>
        {getDialogTitle()}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {platforms.map((platform, index) => (
              <Tab 
                key={platform.key}
                label={platform.label} 
                sx={{ 
                  color: platform.color,
                  '&.Mui-selected': { 
                    color: platform.color,
                    fontWeight: 'bold'
                  }
                }}
              />
            ))}
          </Tabs>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {platforms.map((platform, index) => (
          <TabPanel key={platform.key} value={activeTab} index={index}>
            {renderPlatformForm(platform.key)}
          </TabPanel>
        ))}
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          {t('addIntegrationModal.actions.cancel')}
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={saving}
          sx={{ 
            bgcolor: platforms[activeTab]?.color,
            '&:hover': { 
              bgcolor: platforms[activeTab]?.color,
              opacity: 0.8
            }
          }}
        >
          {saving ? <CircularProgress size={24} /> : t('addIntegrationModal.actions.save')}
        </Button>
      </DialogActions>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default AddIntegrationModal;
